import { TakeEffect, takeLatest } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import { API_GET_EMPLOYEES, KEY_GET_UNASSIGNED_EMPLOYEES } from '../constants';
import { MOCK_RESPONSE_GET_UNASSIGNED_EMPLOYEES } from '../mocks';
import { RequestEmployeesQuery } from '../types/filtersEmployees';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetUnassignedEmployees, 'successSaga' | 'errorSaga'>;
};

type ResponseGetUnassignedEmployees = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_PATH_KEY
>['response']['body'];

export type CallParamsGetUnassignedEmployees = RequestEmployeesQuery & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetUnassignedEmployees = ResponseGetUnassignedEmployees;

type CallErrorGetUnassignedEmployees = {
  message: string;
  code: number;
};

type CallSelectorsGetUnassignedEmployees = {
  data: ResponseGetUnassignedEmployees['data'];
  totalItems: ResponseGetUnassignedEmployees['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload = {};

/**
 * Variant of `getEmployeesCall` that always filters by `unassigned: true`.
 * Returns only employees that have neither a department nor a team associated.
 */
export const getUnassignedEmployeesCall = createGeneralCall<
  CallParamsGetUnassignedEmployees,
  CallResponseGetUnassignedEmployees,
  CallErrorGetUnassignedEmployees,
  CallSelectorsGetUnassignedEmployees
>({
  id: KEY_GET_UNASSIGNED_EMPLOYEES,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_EMPLOYEES(org as unknown as string, { ...payload, unassigned: true });
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_UNASSIGNED_EMPLOYEES : undefined,
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
  cancellable: true,
  takeEffect: takeLatest as unknown as TakeEffect,
  rsmOptions: {
    doubleRun: false,
  },
});
